import React, { useState } from "react";
import PropTypes from "prop-types";
import ContactHelper from "../../../../entities/contact/contact-helper";
import Modal from "../../../../shared/components/dashboard-layout/components/modal/modalComponent";
import Input from "../../../../shared/components/dashboard-layout/components/inputFields/inputComponent";
import "./add-contact-modal.scss";

const AddContactModal = ({ show, onHide, onContactAdded }) => {
    const [contactName, setContactName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");
    const [preferredPlatform, setPreferredPlatform] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Validation function for phone/email requirement
    const validateForm = () => {
        if (!contactName.trim()) {
            return { isValid: false, error: "Contact name is required." };
        }
        if (!phoneNumber && !email.trim()) {
            return { isValid: false, error: "Either phone number or email is required." };
        }
        // Email validation if provided
        if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            return { isValid: false, error: "Please enter a valid email address." };
        }
        return { isValid: true };
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        const validation = validateForm();
        if (!validation.isValid) {
            setError(validation.error);
            return;
        }

        // If both identifiers are present, require preferred platform selection
        if (phoneNumber && email.trim() && !preferredPlatform) {
            setError("Please select a preferred platform.");
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            // Check if contact exists first
            const checkPayload = {};
            if (email.trim()) checkPayload.email = email.trim();
            if (phoneNumber) checkPayload.phone = phoneNumber;
            const existsCheck = await ContactHelper.checkContactExists(checkPayload);
            if (existsCheck) {
                setError("A contact with this email or phone number already exists.");
                setIsLoading(false);
                return;
            }
            // Create contact
            await ContactHelper.createNewContact({
                name: contactName.trim(),
                phone: phoneNumber || undefined,
                email: email.trim() || undefined,
                preferredPlatform: phoneNumber && email.trim() ? preferredPlatform : undefined,
            });
            onContactAdded();
            onHide();
            // Reset form
            setContactName("");
            setPhoneNumber("");
            setEmail("");
            setPreferredPlatform("");
        } catch (err) {
            if (err?.response?.status === 404) {
                // Contact doesn't exist, proceed with creation
                try {
                    await ContactHelper.createNewContact({
                        name: contactName.trim(),
                        phone: phoneNumber || undefined,
                        email: email.trim() || undefined,
                        preferredPlatform: phoneNumber && email.trim() ? preferredPlatform : undefined,
                    });
                    onContactAdded();
                    onHide();
                    // Reset form
                    setContactName("");
                    setPhoneNumber("");
                    setEmail("");
                    setPreferredPlatform("");
                } catch (createErr) {
                    setError(createErr?.response?.data?.message || "Failed to create contact.");
                }
            } else {
                setError(err?.response?.data?.message || "Failed to validate contact.");
            }
        }
        setIsLoading(false);
    };

    const handleClose = () => {
        setContactName("");
        setPhoneNumber("");
        setEmail("");
        setPreferredPlatform("");
        setError(null);
        onHide();
    };

    if (!show) return null;

    return (
        <Modal
            isVisible={show}
            onClose={handleClose}
            header={"Add New Contact"}
            className={"add-contact-modal"}
            cancelButtonLabel={"Close"}
            cancelButtonAction={() => {
                if (isLoading) return;
                handleClose();
            }}
            positiveButtonLabel={isLoading ? "Creating..." : "Create Contact"}
            positiveButtonAction={() => {
                if (isLoading) return;
                handleSubmit();
            }}
        >
            {error && <div className="alert alert-danger mb-3">{error}</div>}

            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <div className="form-group">
                    <Input
                        label={"Contact Name *"}
                        placeholder={"Enter contact name"}
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        disabled={isLoading}
                        required
                    />
                </div>

                <div className="form-group">
                    <Input
                        type={"phone"}
                        label={"Phone Number"}
                        placeholder={"Enter phone number"}
                        value={phoneNumber}
                        onChange={setPhoneNumber}
                        disabled={isLoading}
                        phoneProps={{
                            defaultCountry: "GB",
                            international: true,
                            withCountryCallingCode: true,
                            countryCallingCodeEditable: false,
                            countrySelectProps: { unicodeFlags: true },
                        }}
                    />
                    <small className="text-muted">
                        Either phone number or email is required
                    </small>
                </div>

                <div className="form-group">
                    <Input
                        type={"email"}
                        label={"Email Address"}
                        placeholder={"Enter email address"}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isLoading}
                    />
                    <small className="text-muted">
                        Either phone number or email is required
                    </small>
                </div>
                {phoneNumber && email.trim() && (
                    <div className="form-group">
                        <label>Preferred Platform</label>
                        <div className="d-flex gap-3 align-items-center">
                            <Input
                                type={"radio"}
                                id={"pref-whatsapp"}
                                name={"preferredPlatform"}
                                label={"WhatsApp"}
                                value={"whatsapp"}
                                checked={preferredPlatform === "whatsapp"}
                                onChange={(e) => setPreferredPlatform(e.target.value)}
                                disabled={isLoading}
                            />
                            <Input
                                type={"radio"}
                                id={"pref-email"}
                                name={"preferredPlatform"}
                                label={"Email"}
                                value={"email"}
                                checked={preferredPlatform === "email"}
                                onChange={(e) => setPreferredPlatform(e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                )}
            </form>
        </Modal>
    );
};

AddContactModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onContactAdded: PropTypes.func.isRequired,
};

export default AddContactModal;